/**
 * 회사 목록 조회 API
 * - 로그인 시 선택 가능한 회사 리스트를 서버에서 조회함
 * - 서버 응답의 데이터(resultData.companyList 등)를 반환함
 */
import apiClient from '@shared/api/apiClient';

/**
 * getCompanyList
 * @returns {Promise<Object>} 서버에서 반환된 회사 목록 데이터
 */
async function getCompanyList() {
  const response = await apiClient.get('/v1/auth/login/company');
  return response.data;
}

/**
 * 로그인 API 요청 함수
 * - 사용자 입력값(userId, passwd, companyCode)을 서버에 전달하여 인증 요청 수행
 * - 성공 시 서버에서 반환한 로그인 응답 데이터를 반환함
 * @param {Object} params - 로그인 요청에 필요한 파라미터
 * @param {string} params.userId - 사용자 ID
 * @param {string} params.passwd - 사용자 비밀번호
 * @param {number} params.companyCode - 회사 코드
 * @returns {Promise<Object>} 서버 응답 데이터
 */
async function postLogin({ userId, passwd, companyCode, rememberMe, deviceId, pushToken, osType }) {
  const response = await apiClient.post('/v1/auth/login', {
    userId,
    passwd,
    companyCode,
    rememberMe,
    deviceId,
    pushToken,
    osType,
  });
  return response.data;
}

/**
 * 로그아웃 API
 * - 서버에서 Refresh Token 쿠키를 삭제하고(Set-Cookie Max-Age=0),
 *   Redis/JWT 블랙리스트 등 인증 무효화 처리를 수행합니다.
 * - accessToken은 Authorization 헤더로 전달됩니다.
 */
async function postLogout() {
  const response = await apiClient.post('/v1/auth/logout', null, { withAuth: true });
  return response.data;
}

/**
 * 회사 변경(Switch Company) API
 * 백엔드가 GET 메서드이므로 params 방식으로 요청
 * @param {number} companyCode - 변경할 회사 코드
 * @returns {Promise<Object>}
 */
async function getSwitchCompany(companyCode) {
  const response = await apiClient.get('/v1/users/me/switch-company', {
    params: { company: companyCode },
    withAuth: true,
  });
  return response.data;
}
/**
 * 로그인한 사용자가 변경 가능한 회사 리스트
 * @param {string} userId : 아이디
 * @param {string} passwd : 비밀번호
 * @returns {Promise<Object>}
 */
async function getSwitchableCompanyList(userId, passwd) {
  const response = await apiClient.post(
    '/v1/users/me/companies',
    {
      userId,
      passwd,
    },
    {
      withAuth: true,
    },
  );
  return response.data;
}
/**
 * 인증번호 이메일 발송
 * @param {Object} params
 * @param {string} params.name
 * @param {string} params.email
 * @returns {Promise<Object>} 인증번호 만료시간과 uuid
 */
async function sendEmail(name, email) {
  const response = await apiClient.post('/v1/auth/email/send-code', {
    name,
    email,
  });
  return response.data;
}
/**
 * 이메일 인증번호 검증 API 요청
 * @param {Object} params
 * @param {string} params.name
 * @param {string} params.email
 * @param {string} params.code
 * @param {string} params.requestId
 * @returns {Promise<Object>} 사용자 정보
 */
async function verifyEmailCode({ name, email, code, requestId, purpose }) {
  const response = await apiClient.post('/v1/auth/email/verify-code', {
    name,
    email,
    code,
    requestId,
    purpose,
  });
  return response.data;
}

/**
 * 비밀번호 찾기용 인증번호 이메일 발송
 * @param {Object} params
 * @param {string} params.name
 * @param {string} params.email
 * @param {string} params.id
 * @returns {Promise<Object>} 인증번호 만료시간과 uuid
 */
async function sendEmailForPassword(name, email, id, companyCode) {
  const response = await apiClient.post('/v1/auth/email/send-code/find-password', {
    name,
    email,
    id,
    companyCode,
  });
  return response.data;
}
/**
 * 비밀번호 찾기 후 비밀번호 변경
 *
 * @param {Object} params
 * @param {string} params.email
 * @param {string} params.resetToken
 * @param {string} params.changePw
 */
async function changePassword({ email, resetToken, changePw }) {
  const response = await apiClient.put('/v1/auth/reset/password', {
    email,
    resetToken,
    changePw,
  });

  return response.data;
}

/**
 * 현재 로그인된 사용자 정보 조회 API
 * - 토큰을 검증하고 최신 사용자 정보를 반환합니다.
 * @returns {Promise<Object>}
 */
async function getMe() {
  const response = await apiClient.get('/v1/auth/me', { withAuth: true });
  return response.data;
}

/**
 * 계정전환 API
 * @param {number} companyCode - 전환할 회사 코드
 * @returns {Promise} 새 로그인 정보 (accessToken, user 정보 등)
 */
async function switchCompany(companyCode) {
  const response = await apiClient.get('/v1/users/me/switch-company', {
    params: { company: companyCode },
    withAuth: true,
  });
  return response.data;
}
/**
 * 이메일 인증 완료 후 로그인 마무리 API (Redis 기반)
 * - userId + companyCode를 서버로 전달
 */
async function postLoginComplete({ userId, companyCode, requestId, emailCode }) {
  const response = await apiClient.post(
    '/v1/auth/login/complete',
    {
      userId,
      companyCode,
      requestId,
      emailCode,
    },
    { withAuth: false },
  );
  return response.data;
}

/**
 * [로그인 전용]
 * 사외망 로그인 이메일 인증번호 발송
 *
 * @param {Object} params
 * @param {string} params.userId
 * @param {number} params.companyCode
 * @returns {Promise<Object>} requestId, expireAt
 */
async function sendLoginEmailCode({ userId, companyCode, email }) {
  const response = await apiClient.post('/v1/auth/email/send-code/login', {
    userId,
    companyCode,
    email,
  });

  return response.data;
}
export {
  getCompanyList,
  postLogin,
  postLogout,
  getSwitchCompany,
  getSwitchableCompanyList,
  sendEmail,
  verifyEmailCode,
  sendEmailForPassword,
  changePassword,
  getMe,
  switchCompany,
  postLoginComplete,
  sendLoginEmailCode,
};
